import * as yup from 'yup';
import { useFormik } from 'formik';
import { translate } from 'i18n/jhipster-language';
import { readOnlyTextField } from './yup-helpers';
export const CARD_TYPE_OPTION_VALUES = ['DEBIT', 'CREDIT', 'PREPAID'];
export const CARD_FORM_OPTION_VALUES = ['PHYSICAL', 'VIRTUAL', 'BOTH'];
export const CARD_STATUS_OPTION_VALUES = [
    'REQUESTED',
    'CREATED',
    'ISSUED',
    'PRINTED',
    'PIN_GENERATED',
    'ACTIVE',
    'DEACTIVATED',
    'FROZEN',
    'SUSPENDED',
    'HOTLISTED',
    'REPLACED',
    'EXPIRED',
    'CLOSED',
];
export const CARD_INTERCHANGE_SCOPE_OPTION_VALUES = [
    'INTERNATIONAL',
    'INTERNATIONAL_CHIP_REQUIRED',
    'DOMESTIC',
    'DOMESTIC_CHIP_REQUIRED',
    'PRIVATE',
    'TEST',
];
export const CARD_AUTHORIZATION_REQUIREMENT_OPTION_VALUES = [
    'NORMAL',
    'CHIP_REQUIRED',
    'PIN_REQUIRED',
    'ONLINE_PIN_REQUIRED',
    'OFFLINE_PIN_OK',
    'NO_PIN_REQUIRED',
    'PIN_MAY_BE_REQUIRED',
];
export const CARD_USAGE_RESTRICTION_OPTION_VALUES = [
    'NO_RESTRICTIONS',
    'GOODS_ONLY',
    'ATM_ONLY',
    'CASH_ONLY',
    'GOODS_WITH_CASHBACK',
    'GOODS_PIN_REQUIRED',
    'ATM_PIN_REQUIRED',
    'CASHBACK_ONLY',
];
function partnerCustomerReferenceString(row) {
    return (row.tazkiraNumber?.trim() ||
        row.customerReference?.trim() ||
        row.primaryAccountNumber?.trim() ||
        String(row.id));
}
function refsMatchPartner(card, row) {
    const cardRef = card.cardReference?.trim();
    if (cardRef !== undefined && cardRef !== '' && cardRef === String(row.id)) {
        return true;
    }
    const cref = partnerCustomerReferenceString(row).toLowerCase();
    const fcust = card.customerReference?.trim().toLowerCase();
    const ffund = card.fundingReference?.trim().toLowerCase();
    return ((fcust !== undefined && fcust !== '' && fcust === cref) ||
        (ffund !== undefined && ffund !== '' && ffund === cref) ||
        (fcust !== undefined &&
            fcust !== '' &&
            row.primaryAccountNumber?.trim().toLowerCase() === fcust));
}
export function initialValuesFromCard(card, partnerCustomers) {
    const customerId = partnerCustomers.find(p => refsMatchPartner(card, p))?.id ?? null;
    const product = card.product;
    return {
        customerId,
        cardHolderName: card.cardHolderName?.trim() ?? '',
        productId: product?.id ?? null,
        currencyId: card.currency?.id ?? null,
        cardType: card.cardType ?? product?.cardType ?? 'DEBIT',
        cardForm: card.cardForm ?? product?.cardForm ?? 'PHYSICAL',
        status: card.status ?? product?.status ?? 'ACTIVE',
        interchangeScope: product?.interchangeScope ?? 'DOMESTIC',
        authorizationRequirement: product?.authorizationRequirement ?? 'NORMAL',
        usageRestriction: product?.usageRestriction ?? 'NO_RESTRICTIONS',
    };
}
function buildCardRequestBody(values, props, productMode) {
    if (values.customerId == null ||
        values.productId == null ||
        values.currencyId == null) {
        return null;
    }
    const baseProduct = props.getProduct(values.productId);
    const currencyRow = props.getCurrencyRow(values.currencyId);
    const refs = partnerRefs(props.getPartnerRow(values.customerId));
    if (!baseProduct?.id || !currencyRow?.id || !refs) {
        return null;
    }
    const product = productMode === 'asFetched'
        ? baseProduct
        : {
            ...baseProduct,
            cardType: values.cardType,
            cardForm: values.cardForm,
            status: values.status,
            interchangeScope: values.interchangeScope,
            authorizationRequirement: values.authorizationRequirement,
            usageRestriction: values.usageRestriction,
        };
    const currency = accRowToCardCurrency(currencyRow);
    const issueDate = new Date().toISOString().slice(0, 10);
    return {
        cardReference: refs.cardReference,
        customerReference: refs.customerReference,
        fundingReference: refs.fundingReference,
        cardHolderName: values.cardHolderName.trim(),
        product,
        currency,
        issueDate,
        commissionGroupCode: product.commissionGroupCode ?? null,
        rewardGroupCode: product.rewardGroupCode ?? null,
        pricingGroupCode: product.pricingGroupCode ?? null,
        atmAllowed: product.atmAllowed ?? null,
        posAllowed: product.posAllowed ?? null,
        ecommerceAllowed: product.ecommerceAllowed ?? null,
        contactlessAllowed: product.contactlessAllowed ?? null,
        internationalAllowed: product.internationalAllowed ?? null,
        cashWithdrawalAllowed: product.cashWithdrawalAllowed ?? null,
        interchangeScope: product.interchangeScope ?? null,
        authorizationRequirement: product.authorizationRequirement ?? null,
        usageRestriction: product.usageRestriction ?? null,
    };
}
export function buildMergedUpdatePayload(card, values, props) {
    const inner = buildCardRequestBody(values, props, 'mergeFromForm');
    if (!inner || card.id == null) {
        return null;
    }
    return {
        ...card,
        ...inner,
        id: card.id,
        product: inner.product,
        currency: inner.currency,
        issueDate: card.issueDate ?? inner.issueDate,
    };
}
const initialValues = () => ({
    customerId: null,
    cardHolderName: '',
    productId: null,
    currencyId: null,
    cardType: 'DEBIT',
    cardForm: 'PHYSICAL',
    status: 'CREATED',
    interchangeScope: 'DOMESTIC',
    authorizationRequirement: 'NORMAL',
    usageRestriction: 'NO_RESTRICTIONS',
});
function accRowToCardCurrency(row) {
    return {
        id: row.id,
        name: row.name ?? null,
        code: row.code ?? null,
        active: true,
    };
}
function partnerRefs(row) {
    if (!row) {
        return undefined;
    }
    const customerReference = partnerCustomerReferenceString(row);
    const fundingReference = row.fundingReference?.trim() || row.primaryAccountNumber?.trim() || '';
    const cardReference = String(row.id);
    return { cardReference, customerReference, fundingReference };
}
function buildCreateBody(values, props) {
    return buildCardRequestBody(values, props, 'asFetched');
}
export const cardFormValidationSchema = yup.object().shape({
    customerId: yup
        .number()
        .typeError(translate('entity.validation.number'))
        .required(translate('entity.validation.required')),
    cardHolderName: readOnlyTextField().required(translate('entity.validation.required')).nullable(),
    productId: yup
        .number()
        .typeError(translate('entity.validation.number'))
        .required(translate('entity.validation.required')),
    currencyId: yup
        .number()
        .typeError(translate('entity.validation.number'))
        .required(translate('entity.validation.required')),
    cardType: yup.string().required(translate('entity.validation.required')).nullable(),
    cardForm: yup.string().required(translate('entity.validation.required')).nullable(),
    status: yup.string().required(translate('entity.validation.required')).nullable(),
    interchangeScope: yup.string().required(translate('entity.validation.required')).nullable(),
    authorizationRequirement: yup.string().required(translate('entity.validation.required')).nullable(),
    usageRestriction: yup.string().required(translate('entity.validation.required')).nullable(),
});
/** Create card: validate only fields the user edits; product drives type/form/scopes/restrictions and status on the wire. */
export const cardCreateFormValidationSchema = yup.object().shape({
    customerId: yup
        .number()
        .typeError(translate('entity.validation.number'))
        .required(translate('entity.validation.required')),
    cardHolderName: readOnlyTextField().required(translate('entity.validation.required')).nullable(),
    productId: yup
        .number()
        .typeError(translate('entity.validation.number'))
        .required(translate('entity.validation.required')),
    currencyId: yup
        .number()
        .typeError(translate('entity.validation.number'))
        .required(translate('entity.validation.required')),
});
export const form = (props) => useFormik({
    initialValues: initialValues(),
    enableReinitialize: true,
    validationSchema: cardFormValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit(data) {
        const body = buildCreateBody(data, props);
        if (body) {
            props.createEntity(body);
        }
    },
});
export const createForm = (props) => useFormik({
    initialValues: initialValues(),
    enableReinitialize: true,
    validationSchema: cardCreateFormValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit(data) {
        const body = buildCreateBody(data, props);
        if (body) {
            props.createEntity(body);
        }
    },
});
